import React, { useState, useRef, useEffect } from 'react';
import { useCalendars } from '../../contexts/CalendarContext';
import { useAuth } from '../../contexts/AuthContext';
import JoinCalendarModal from './JoinCalendarModal';
import { logCalendarSwitched } from '../../services/analytics';

const CalendarSelector = ({ onManageClick }) => {
    const { calendars, selectedCalendar, setSelectedCalendar, loading } = useCalendars();
    const { currentUser } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [showJoinModal, setShowJoinModal] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelectCalendar = (calendar) => {
        setSelectedCalendar(calendar);
        logCalendarSwitched();
        setIsOpen(false);
    };

    const getCalendarColor = (color) => {
        const colors = {
            blue: 'bg-blue-500',
            green: 'bg-green-500',
            purple: 'bg-purple-500',
            red: 'bg-red-500',
            orange: 'bg-orange-500',
            pink: 'bg-pink-500',
            teal: 'bg-teal-500',
            indigo: 'bg-indigo-500'
        };
        return colors[color] || colors.blue;
    };

    const isSharedCalendar = (calendar) => {
        return calendar.ownerId !== currentUser?.uid && calendar.members?.length > 1;
    };

    if (loading) {
        return (
            <div className="flex items-center space-x-2 px-3 py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Loading...</span>
            </div>
        );
    }

    if (calendars.length === 0) {
        return (
            <button
                onClick={onManageClick}
                className="px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 rounded-lg transition-colors"
            >
                Create Calendar
            </button>
        );
    }

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Calendar Selector Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-2 px-3 py-2.5 md:py-2 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 transition-colors touch-manipulation min-h-[44px] md:min-h-0"
                aria-label="Select calendar"
                aria-expanded={isOpen}
            >
                {selectedCalendar && (
                    <>
                        <div className={`w-4 h-4 md:w-3 md:h-3 rounded-full flex-shrink-0 ${getCalendarColor(selectedCalendar.color)}`} />
                        <span className="font-medium text-sm md:text-base truncate max-w-[120px] sm:max-w-[200px]">{selectedCalendar.name}</span>
                        {isSharedCalendar(selectedCalendar) && (
                            <svg
                                className="w-4 h-4 flex-shrink-0 text-blue-500 dark:text-blue-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                title="Shared calendar"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        )}
                    </>
                )}
                <svg
                    className={`w-5 h-5 md:w-4 md:h-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 md:w-64 max-w-md bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 animate-fade-in">
                    <div className="p-2">
                        <div className="mb-2">
                            <p className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                My Calendars
                            </p>
                        </div>

                        {/* Calendar List */}
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                            {calendars.map((calendar) => (
                                <button
                                    key={calendar.id}
                                    onClick={() => handleSelectCalendar(calendar)}
                                    className={`w-full flex items-center space-x-3 px-3 py-3 md:py-2 rounded-md text-left transition-colors touch-manipulation min-h-[48px] md:min-h-0 ${selectedCalendar?.id === calendar.id
                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100'
                                        : 'hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 text-gray-900 dark:text-gray-100'
                                        }`}
                                >
                                    <div className={`w-4 h-4 md:w-3 md:h-3 rounded-full flex-shrink-0 ${getCalendarColor(calendar.color)}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1">
                                            <p className="font-medium truncate">{calendar.name}</p>
                                            {isSharedCalendar(calendar) && (
                                                <svg
                                                    className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    title="Shared calendar"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                </svg>
                                            )}
                                        </div>
                                        {calendar.description && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                {calendar.description}
                                            </p>
                                        )}
                                    </div>
                                    {selectedCalendar?.id === calendar.id && (
                                        <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1">
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    setShowJoinModal(true);
                                }}
                                className="w-full flex items-center space-x-2 px-3 py-3 md:py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 transition-colors touch-manipulation min-h-[48px] md:min-h-0"
                            >
                                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                <span className="font-medium">Join Calendar</span>
                            </button>
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    onManageClick();
                                }}
                                className="w-full flex items-center space-x-2 px-3 py-3 md:py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 transition-colors touch-manipulation min-h-[48px] md:min-h-0"
                            >
                                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="font-medium">Manage Calendars</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Join Calendar Modal */}
            <JoinCalendarModal
                isOpen={showJoinModal}
                onClose={() => setShowJoinModal(false)}
            />
        </div>
    );
};

export default CalendarSelector;
